import React, { useState, useEffect } from 'react';
import { Container, Row, Col, Card, Table, Button, Form, Modal, Spinner, Alert } from 'react-bootstrap';
import ccmService from '../../services/ccmService';
import './TimeTracking.css';
import AppDateInput from '../../components/Common/AppDateInput/AppDateInput';

const TimeTracking = () => {
  const [loading, setLoading] = useState(true);
  const [timeLogs, setTimeLogs] = useState([]);
  const [summary, setSummary] = useState({});
  const [showAddModal, setShowAddModal] = useState(false);
  const [timeCaptureReasons, setTimeCaptureReasons] = useState([]);
  const [filters, setFilters] = useState({
    enrollmentId: '',
    dateFrom: '',
    dateTo: '',
    billingMonth: ''
  });
  const [newTimeEntry, setNewTimeEntry] = useState({
    enrollment_id: '',
    service_date: new Date().toISOString().split('T')[0],
    service_time: '',
    time_capture_reason_id: '',
    service_type: '',
    description: ''
  });

  useEffect(() => {
    fetchTimeCaptureReasons();
    if (filters.enrollmentId) {
      fetchTimeLogs();
    } else {
      // If no enrollmentId, still show UI (empty state)
      setLoading(false);
    }
  }, [filters.enrollmentId]);

  const fetchTimeCaptureReasons = async () => {
    try {
      const response = await ccmService.getTimeCaptureReasons();
      if (response.success) {
        setTimeCaptureReasons(Array.isArray(response.data) ? response.data : []);
      }
    } catch (error) {
      console.error('Error fetching time capture reasons:', error);
    }
  };

  const fetchTimeLogs = async () => {
    try {
      setLoading(true);
      const response = await ccmService.getTimeLogs(filters);
      if (response.success) {
        setTimeLogs(response.data || []);
        setSummary(response.summary || {});
      }
    } catch (error) {
      console.error('Error fetching time logs:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleAddTime = async () => {
    try {
      const response = await ccmService.addTimeManually(newTimeEntry);
      if (response.success) {
        setShowAddModal(false);
        setNewTimeEntry({
          enrollment_id: '',
          service_date: new Date().toISOString().split('T')[0],
          service_time: '',
          time_capture_reason_id: '',
          service_type: '',
          description: ''
        });
        fetchTimeLogs();
      }
    } catch (error) {
      console.error('Error adding time entry:', error);
      alert('Failed to add time entry: ' + (error.response?.data?.message || error.message));
    }
  };

  const formatTime = (minutes) => {
    if (!minutes) return '0 min';
    const hours = Math.floor(minutes / 60);
    const mins = minutes % 60;
    if (hours > 0) {
      return `${hours}h ${mins}min`;
    }
    return `${mins}min`;
  };

  return (
    <Container fluid className="ccm-tt-container">
      <Row>
        <Col>
          <Card>
            <Card.Header className="d-flex justify-content-between align-items-center">
              <h4>Time Tracking</h4>
              <Button variant="primary" onClick={() => setShowAddModal(true)}>
                Add Time Entry
              </Button>
            </Card.Header>
            <Card.Body>
              {/* Filters */}
              <Row className="mb-3">
                <Col md={3}>
                  <Form.Group>
                    <Form.Label>Enrollment ID</Form.Label>
                    <Form.Control
                      type="text"
                      placeholder="Enter Enrollment ID"
                      value={filters.enrollmentId}
                      onChange={(e) => {
                        setFilters(prev => ({ ...prev, enrollmentId: e.target.value }));
                      }}
                    />
                  </Form.Group>
                </Col>
                <Col md={3}>
                  <Form.Group>
                    <Form.Label>Date From</Form.Label>
                    <AppDateInput
                      className="form-control"
                      value={filters.dateFrom}
                      onChange={(v) => setFilters(prev => ({ ...prev, dateFrom: v }))}
                    />
                  </Form.Group>
                </Col>
                <Col md={3}>
                  <Form.Group>
                    <Form.Label>Date To</Form.Label>
                    <AppDateInput
                      className="form-control"
                      value={filters.dateTo}
                      onChange={(v) => setFilters(prev => ({ ...prev, dateTo: v }))}
                    />
                  </Form.Group>
                </Col>
                <Col md={3} className="d-flex align-items-end">
                  <Button variant="primary" onClick={fetchTimeLogs}>
                    Filter
                  </Button>
                </Col>
              </Row>

              {/* Summary */}
              {summary.total_time !== undefined && (
                <Alert variant="info" className="mb-3">
                  <strong>Summary:</strong> Total Time: {formatTime(summary.total_time)} | 
                  Monthly Time: {formatTime(summary.monthly_time)} | 
                  Total Entries: {summary.total_entries || 0}
                </Alert>
              )}

              {/* Table */}
              {loading ? (
                <div className="text-center py-5">
                  <Spinner animation="border" />
                </div>
              ) : (
                <Table striped bordered hover responsive>
                  <thead>
                    <tr>
                      <th>Date</th>
                      <th>Time</th>
                      <th>Reason</th>
                      <th>Service Type</th>
                      <th>Description</th>
                      <th>Performed By</th>
                      <th>Billing Month</th>
                      <th>Billed</th>
                    </tr>
                  </thead>
                  <tbody>
                    {timeLogs.length === 0 ? (
                      <tr>
                        <td colSpan="8" className="text-center">
                          {filters.enrollmentId ? 'No time logs found' : 'Please enter an Enrollment ID to view time logs'}
                        </td>
                      </tr>
                    ) : (
                      timeLogs.map((log) => (
                        <tr key={log.id}>
                          <td>{log.service_date ? new Date(log.service_date).toLocaleDateString() : 'N/A'}</td>
                          <td>{formatTime(log.service_time)}</td>
                          <td>{log.time_capture_reason || 'N/A'}</td>
                          <td>{log.service_type || 'N/A'}</td>
                          <td>{log.description || 'N/A'}</td>
                          <td>{log.performed_by || 'N/A'}</td>
                          <td>{log.billing_month || 'N/A'}</td>
                          <td>{log.is_billed ? 'Yes' : 'No'}</td>
                        </tr>
                      ))
                    )}
                  </tbody>
                </Table>
              )}
            </Card.Body>
          </Card>
        </Col>
      </Row>

      {/* Add Time Modal */}
      <Modal show={showAddModal} onHide={() => setShowAddModal(false)} size="lg" backdrop="static">
        <Modal.Header closeButton>
          <Modal.Title>Add Time Entry</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <Form>
            <Row>
              <Col md={6}>
                <Form.Group className="mb-3">
                  <Form.Label>Enrollment ID *</Form.Label>
                  <Form.Control
                    type="text"
                    value={newTimeEntry.enrollment_id}
                    onChange={(e) => setNewTimeEntry(prev => ({ ...prev, enrollment_id: e.target.value }))}
                    required
                  />
                </Form.Group>
              </Col>
              <Col md={6}>
                <Form.Group className="mb-3">
                  <Form.Label>Service Date *</Form.Label>
                  <AppDateInput
                    className="form-control"
                    value={newTimeEntry.service_date}
                    onChange={(v) => setNewTimeEntry(prev => ({ ...prev, service_date: v }))}
                  />
                </Form.Group>
              </Col>
            </Row>
            <Row>
              <Col md={6}>
                <Form.Group className="mb-3">
                  <Form.Label>Service Time (minutes) *</Form.Label>
                  <Form.Control
                    type="number"
                    min="1"
                    value={newTimeEntry.service_time}
                    onChange={(e) => setNewTimeEntry(prev => ({ ...prev, service_time: e.target.value }))}
                    required
                  />
                </Form.Group>
              </Col>
              <Col md={6}>
                <Form.Group className="mb-3">
                  <Form.Label>Time Capture Reason</Form.Label>
                  <Form.Select
                    value={newTimeEntry.time_capture_reason_id}
                    onChange={(e) => setNewTimeEntry(prev => ({ ...prev, time_capture_reason_id: e.target.value }))}
                  >
                    <option value="">Select Reason</option>
                    {(Array.isArray(timeCaptureReasons) ? timeCaptureReasons : []).map((reason) => (
                      <option key={reason.id} value={reason.id}>
                        {reason.reason_description}
                      </option>
                    ))}
                  </Form.Select>
                </Form.Group>
              </Col>
            </Row>
            <Row>
              <Col md={6}>
                <Form.Group className="mb-3">
                  <Form.Label>Service Type</Form.Label>
                  <Form.Control
                    type="text"
                    placeholder="e.g., Phone Call, Email, Chart Review"
                    value={newTimeEntry.service_type}
                    onChange={(e) => setNewTimeEntry(prev => ({ ...prev, service_type: e.target.value }))}
                  />
                </Form.Group>
              </Col>
            </Row>
            <Row>
              <Col>
                <Form.Group className="mb-3">
                  <Form.Label>Description</Form.Label>
                  <Form.Control
                    as="textarea"
                    rows={3}
                    value={newTimeEntry.description}
                    onChange={(e) => setNewTimeEntry(prev => ({ ...prev, description: e.target.value }))}
                  />
                </Form.Group>
              </Col>
            </Row>
          </Form>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setShowAddModal(false)}>
            Cancel
          </Button>
          <Button variant="primary" onClick={handleAddTime}>
            Add Time Entry
          </Button>
        </Modal.Footer>
      </Modal>
    </Container>
  );
};

export default TimeTracking;

